import { z } from 'zod';
import { SalesOrderItemType } from '../../constants/sales-order-item-type.js';
import { SAMPLE_PURPOSES } from '../../constants/sample-purpose.js';
import { roundTaxPercent } from '../../sales-order-totals/round-tax-percent.js';
import { TAX_PERCENT_DECIMALS } from '../../sales-order-totals/tax-percent-decimals.js';
import { nullableUuidString, requiredUuidString } from '../zod-helpers/index.js';
import { SALES_ORDER_HEADER_CONSTRAINTS } from './header-constraints.js';
import { salesOrderLineQuantitySchema } from './item-quantity.js';
import { duplicateSingleAdjustmentKinds } from './update-order-input/single-adjustment-kinds.js';
// Staged client-side until the order is created on the review step's final submit -- there is no
// salesOrderId yet, so these aren't validated against AddSalesOrderItemInput.
export const salesOrderCreateLineSchema = z.object({
    productId: requiredUuidString('Product is required.', 'Invalid product selection.'),
    productNum: z.string(),
    name: z.string().nullable(),
    qty: salesOrderLineQuantitySchema,
    unitPrice: z.number().min(0, 'Unit price cannot be negative.'),
    typeId: z.number().int(),
    note: z.string(),
    uomId: requiredUuidString('Unit of measure is required.', 'Invalid unit of measure selection.'),
});
// Staged like `lines` above and sent as `adjustments` at submit (book-mapper.ts): one Shipping, one
// Discount, and one Tax row per tax rate plus at most one manual row (enforced below).
export const salesOrderStagedAdjustmentSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('shipping'),
        amount: z.number().min(0, 'Shipping fee cannot be negative.'),
    }),
    z.object({
        kind: z.literal('tax'),
        percent: z
            .number()
            .min(0, 'Tax cannot be negative.')
            .max(100, 'Tax percentage cannot exceed 100.')
            .refine((value) => roundTaxPercent(value) === value, {
            message: `Tax percent can have at most ${TAX_PERCENT_DECIMALS} decimal places.`,
        }),
        taxRateId: nullableUuidString('Invalid tax rate selection.'),
        name: z.string(),
    }),
    z.object({
        kind: z.literal('discount'),
        amount: z.number().min(0, 'Discount cannot be negative.'),
        discountType: z.enum(['flat', 'percentage']),
    }),
]);
const STAGED_KIND_TYPE_ID = {
    shipping: SalesOrderItemType.SHIPPING,
    tax: SalesOrderItemType.TAX,
    discount: SalesOrderItemType.DISCOUNT,
};
export const salesOrderCreateFormSchema = z
    .object({
    customerId: requiredUuidString(`${SALES_ORDER_HEADER_CONSTRAINTS.customer.label} is required.`, 'Invalid customer selection.'),
    brandId: requiredUuidString(`${SALES_ORDER_HEADER_CONSTRAINTS.brand.label} is required.`, 'Invalid brand selection.'),
    locationGroupId: requiredUuidString(`${SALES_ORDER_HEADER_CONSTRAINTS.locationGroup.label} is required.`, 'Invalid warehouse selection.'),
    paymentTermsId: requiredUuidString(`${SALES_ORDER_HEADER_CONSTRAINTS.paymentTerms.label} is required.`, 'Invalid payment terms selection.'),
    note: z.string(),
    internalNotes: z.string(),
    deliveryPreferences: z.string(),
    customerContact: z.string(),
    // The contact's real id, kept alongside the free-text name above -- see book-mapper.ts,
    // which carries it into the order's customFields (the customerContact column itself is
    // plain text, with no foreign key to Contact).
    customerContactId: z.string(),
    lines: z.array(salesOrderCreateLineSchema).min(1, 'Add at least one product line.'),
    samplePurpose: z.enum(SAMPLE_PURPOSES).nullable(),
    adjustments: z.array(salesOrderStagedAdjustmentSchema),
})
    .superRefine((values, context) => {
    for (const { index } of duplicateSingleAdjustmentKinds(values.adjustments.map((adjustment) => STAGED_KIND_TYPE_ID[adjustment.kind]))) {
        context.addIssue({
            code: 'custom',
            message: `Only one ${values.adjustments[index].kind} adjustment is allowed.`,
            path: ['adjustments', index, 'kind'],
        });
    }
    const seenTaxRows = new Set();
    values.adjustments.forEach((adjustment, index) => {
        if (adjustment.kind === 'tax') {
            if (seenTaxRows.has(adjustment.taxRateId)) {
                context.addIssue({
                    code: 'custom',
                    message: adjustment.taxRateId === null
                        ? 'Only one manual tax is allowed.'
                        : 'Each tax rate can appear only once.',
                    path: ['adjustments', index, 'taxRateId'],
                });
            }
            seenTaxRows.add(adjustment.taxRateId);
        }
        if (adjustment.kind === 'discount' &&
            adjustment.discountType === 'percentage' &&
            adjustment.amount > 100) {
            context.addIssue({
                code: 'custom',
                message: 'Discount percentage cannot exceed 100.',
                path: ['adjustments', index, 'amount'],
            });
        }
    });
});
export const emptySalesOrderCreateFormValues = () => ({
    customerId: '',
    brandId: '',
    locationGroupId: '',
    paymentTermsId: '',
    note: '',
    internalNotes: '',
    deliveryPreferences: '',
    customerContact: '',
    customerContactId: '',
    lines: [],
    samplePurpose: null,
    adjustments: [],
});
//# sourceMappingURL=create-form.js.map